"use client";

import { CSSProperties, useState } from "react";
import { Plus, X } from "lucide-react";

import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import {
    TabItem,
    TabsAppearance,
    TabsBlock as TabsBlockType,
} from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../../store/useLomdaV3Editor";
import { useBlockEditable } from "../../../hooks/useBlockEditable";
import { resolveTabsAppearance } from "../../../lib/blockRegistry";
import { TabIcon } from "../../../lib/tabIcons";
import { IconPicker } from "../shared/iconPicker";
import { ICON_SIZE } from "../../../lib/iconSizes";
import NestedBlocks from "../layout/nestedBlocks";
import SlotHeaderDrop from "../dnd/slotHeaderDrop";
import { useLomdaLocale } from "../../../hooks/useLomdaLocale";

const tabBlocks = (it: TabItem) => it.blocks ?? [];

const barStyle = (a: TabsAppearance): CSSProperties => ({
    borderColor: a.border,
    borderWidth: a.borderWidth,
    borderStyle: "solid",
});
const tabStyle = (a: TabsAppearance, active: boolean): CSSProperties => ({
    backgroundColor: active ? a.activeTabBackground : a.tabBackground,
});
// Per-tab text colour; the inactive shade keeps the muted hierarchy as a
// 72% mix of the chosen colour. Unset → the theme classes below apply.
const tabTextColor = (it: TabItem, active: boolean): string | undefined => {
    const c = it.appearance?.textColor;
    if (!c) return undefined;
    return active ? c : `color-mix(in srgb, ${c} 72%, transparent)`;
};

const BAR_CLASS =
    "flex items-center gap-1 overflow-x-auto rounded-lg bg-card p-1.5 shadow-[0_8px_16px_rgba(153,187,255,0.12)]";
const TAB_CLASS =
    "flex min-w-[120px] flex-1 items-center justify-center gap-3 rounded-lg px-4 py-2 transition";

/** Icon picker: dashed "+" placeholder when empty, coloured glyph when set. */
function TabIconPicker({
    icon,
    color,
    onPick,
}: {
    icon: string;
    color: string;
    onPick: (icon: string) => void;
}) {
    return (
        <IconPicker value={icon} onPick={onPick}>
            <button
                type="button"
                aria-label={t("lomda-v3-editor.tabs.select-icon")}
                onMouseDown={(e) => e.preventDefault()}
                className="shrink-0"
            >
                {icon ? (
                    <span style={{ color }}>
                        <TabIcon name={icon} className={ICON_SIZE.tabIcon} />
                    </span>
                ) : (
                    <span className="flex size-8 items-center justify-center rounded-[5px] border border-dashed border-[hsl(var(--lomda-border-dashed))] bg-[hsl(var(--lomda-muted))]">
                        <Plus className="size-3.5 text-[hsl(var(--lomda-muted-text))]" />
                    </span>
                )}
            </button>
        </IconPicker>
    );
}

export default function TabsBlock({ block }: { block: TabsBlockType }) {
    const locale = useLomdaLocale();
    const updateBlock = useLomdaV3Editor((s) => s.updateBlock);
    const patchBlockData = useLomdaV3Editor((s) => s.patchBlockData);
    const setFocusedQuestion = useLomdaV3Editor((s) => s.setFocusedQuestion);

    const editable = useBlockEditable();
    const items = block.data.items;
    const theme = useLomdaV3Editor((s) => s.metadata.theme);
    const appearance = resolveTabsAppearance(block.data.appearance, theme);
    // The preview keeps its own open tab; the editor's lives in the store so
    // Add and "Go to block" can see and open it.
    const [active, setActive] = useState(0);
    const openId = useLomdaV3Editor((s) => s.openSlots[block.id]);
    const setOpenSlot = useLomdaV3Editor((s) => s.setOpenSlot);
    const open = (id: string) => setOpenSlot(block.id, id);

    const setItems = (next: TabItem[]) => updateBlock(block.id, { items: next });
    const patchItem = (id: string, patch: Partial<TabItem>) =>
        patchBlockData(block.id, "tabs", (d) => ({
            items: d.items.map((it) => (it.id === id ? { ...it, ...patch } : it)),
        }));

    const labelFor = (label: string, i: number) =>
        label || t("lomda-v3-editor.tabs.tab-label", locale, { number: i + 1 });

    // ── Preview ──
    if (!editable) {
        const filled = items.filter((it) => it.label || tabBlocks(it).length);
        if (filled.length === 0) return null;
        const current = filled[Math.min(active, filled.length - 1)] ?? filled[0];
        return (
            <div className="flex flex-col gap-10">
                <div className={BAR_CLASS} style={barStyle(appearance)}>
                    {filled.map((it, i) => {
                        const isActive = it.id === current?.id;
                        const custom = tabTextColor(it, isActive);
                        return (
                            <button
                                key={it.id}
                                type="button"
                                onClick={() => setActive(i)}
                                className={cn(
                                    TAB_CLASS,
                                    isActive
                                        ? "text-[hsl(var(--lomda-text))]"
                                        : "text-[hsl(var(--lomda-muted-text))]"
                                )}
                                style={{
                                    ...tabStyle(appearance, isActive),
                                    color: custom,
                                }}
                            >
                                {appearance.showIcons && it.icon && (
                                    <span
                                        style={{
                                            color:
                                                custom ??
                                                (isActive
                                                    ? "hsl(var(--lomda-text))"
                                                    : "hsl(var(--lomda-muted-text))"),
                                        }}
                                    >
                                        <TabIcon
                                            name={it.icon}
                                            className={cn(
                                                ICON_SIZE.tabIcon,
                                                "shrink-0"
                                            )}
                                        />
                                    </span>
                                )}
                                <span className="truncate lomda-v3-text-p">
                                    {labelFor(it.label, i)}
                                </span>
                            </button>
                        );
                    })}
                </div>
                {current && (
                    <NestedBlocks
                        key={current.id}
                        containerId={block.id}
                        slotId={current.id}
                        blocks={tabBlocks(current)}
                        editable={false}
                    />
                )}
            </div>
        );
    }

    // ── Editor ──
    const current = items.find((it) => it.id === openId) ?? items[0];

    return (
        // Same focus pattern as flip cards: the capture handler clears, the
        // clicked tab's own handler re-sets — so clicking the content area or
        // another block's way in drops the "Tab appearance" tier.
        <div
            className="flex flex-col gap-6"
            onPointerDownCapture={() => setFocusedQuestion(null)}
        >
            <div className={BAR_CLASS} style={barStyle(appearance)}>
                {items.map((it, i) => {
                    const isActive = it.id === current?.id;
                    const custom = tabTextColor(it, isActive);
                    return (
                        <SlotHeaderDrop
                            key={it.id}
                            containerId={block.id}
                            slotId={it.id}
                            count={tabBlocks(it).length}
                            open={isActive}
                            onOpen={() => open(it.id)}
                        >
                            <div
                                onPointerDown={() => {
                                    open(it.id);
                                    setFocusedQuestion(it.id);
                                }}
                                className={cn(
                                    "group/tab",
                                    TAB_CLASS,
                                    isActive ? "text-[hsl(var(--lomda-text))]" : "text-[hsl(var(--lomda-muted-text))]"
                                )}
                                style={{
                                    ...tabStyle(appearance, isActive),
                                    color: custom,
                                }}
                            >
                                {appearance.showIcons && (
                                    <TabIconPicker
                                        icon={it.icon}
                                        color={
                                            custom ??
                                            (isActive
                                                ? "hsl(var(--lomda-text))"
                                                : "hsl(var(--lomda-muted-text))")
                                        }
                                        onPick={(icon) => patchItem(it.id, { icon })}
                                    />
                                )}
                                <input
                                    value={it.label}
                                    onFocus={() => open(it.id)}
                                    onChange={(e) =>
                                        patchItem(it.id, { label: e.target.value })
                                    }
                                    placeholder={t(
                                        "lomda-v3-editor.tabs.label-placeholder"
                                    )}
                                    className={cn(
                                        "min-w-0 flex-1 bg-transparent text-center lomda-v3-text-p outline-none placeholder:text-[hsl(var(--lomda-muted-text))]",
                                        isActive
                                            ? "text-[hsl(var(--lomda-text))]"
                                            : "text-[hsl(var(--lomda-muted-text))]"
                                    )}
                                    style={{ color: custom }}
                                />
                                {items.length > 1 && (
                                    <button
                                        type="button"
                                        aria-label={t("lomda-v3-editor.block.delete")}
                                        onClick={() => {
                                            if (it.id === current?.id)
                                                open((items[i - 1] ?? items[i + 1]).id);
                                            setItems(
                                                items.filter((x) => x.id !== it.id)
                                            );
                                        }}
                                        className="flex size-5 shrink-0 items-center justify-center rounded-full text-[hsl(var(--lomda-muted-text))] opacity-0 transition hover:text-destructive group-hover/tab:opacity-100"
                                    >
                                        <X className="size-3.5" />
                                    </button>
                                )}
                            </div>
                        </SlotHeaderDrop>
                    );
                })}
            </div>

            {current && (
                <NestedBlocks
                    key={current.id}
                    containerId={block.id}
                    slotId={current.id}
                    blocks={tabBlocks(current)}
                    editable
                />
            )}
        </div>
    );
}
